import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { RadioGroupField, TextField } from "$components/rhf-fields";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import {
  PayerType,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import { useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import type { RxDocument } from "rxdb";
import { object, string } from "yup";
import { updateWithInsurerChargeIds } from "./update-with-insurer-charge-ids";
import {
  buildWaiveNotes,
  WAIVE_REASON_MESSAGES,
  WAIVE_REASON_ORDER,
  WAIVE_REASON_VALUES,
  type WaiveReasonValue,
} from "./waive-reason-options";

type FormValues = { reason: WaiveReasonValue | ""; otherText?: string };

const useStyles = makeStyles((theme) => ({
  warningIcon: { marginRight: theme.spacing(2) },
  warningMessage: {
    color: theme.palette.grey[600],
    fontWeight: 600,
    marginLeft: theme.spacing(0.5),
  },
}));

/**
 * V2 waive flow. Routes the patient's residual due to a WAIVER
 * transaction with the chosen reason persisted into `notes`. No new
 * BE enum — PNE-326's "controlled list with 'Other → free text'" is
 * rendered client-side and the chosen label round-trips through
 * `SyncableTransaction.notes`.
 */
export const WaiveInvoiceModalV2 = ({
  doc,
  open,
  onClose,
}: {
  doc: RxDocument<RxInvoice>;
  open: boolean;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const user = useUser();
  const { getCurrentTime } = useServerTime();
  const [busy, setBusy] = useState(false);

  const validationSchema = useMemo(
    () =>
      object({
        reason: string()
          .oneOf(WAIVE_REASON_ORDER as string[])
          .required(),
        otherText: string().when("reason", {
          is: WAIVE_REASON_VALUES.OTHER,
          then: (schema) => schema.trim().min(1).required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [],
  );

  const formMethods = useForm<FormValues>({
    mode: "onTouched",
    resolver: yupResolver(validationSchema) as unknown as ReturnType<
      typeof yupResolver
    >,
    defaultValues: { reason: "", otherText: "" },
  });

  const reasonOptions = useMemo(
    () =>
      WAIVE_REASON_ORDER.map((value) => ({
        value,
        label: formatMessage(WAIVE_REASON_MESSAGES[value]),
        "data-testid": `invoicing.waiveV2.reason.${value}`,
      })),
    [formatMessage],
  );

  const watchedReason = useWatch({
    control: formMethods.control,
    name: "reason",
  });

  const onSubmit = async (values: FormValues) => {
    const reason = values.reason;
    if (!reason) return;
    setBusy(true);
    try {
      const timestamp = getCurrentTime().toISOString();
      const transactionId = randomUniqueId();
      await updateWithInsurerChargeIds(doc, {
        actionedByUserId: user.id,
        timestamp,
        createTransactionId: randomUniqueId,
        update: (invoice, insurerCharges) => {
          const current = invoice.toRecord();
          const patient = current.payers.find(
            (payer) => payer.type === PayerType.Patient,
          ) as (typeof current.payers)[number];
          return invoice
            .waive(
              {
                id: transactionId,
                uuid: null,
                type: TransactionType.Waiver,
                status: TransactionStatus.Success,
                timestamp,
                actionedByUserId: user.id,
                amount: {
                  currency: current.balance.currency,
                  value: patient.balance.due,
                },
                payerName: "WAIVER",
                payerType: PayerType.Waiver,
                notes: buildWaiveNotes(reason, values.otherText),
              },
              insurerCharges,
            )
            .toRecord();
        },
      });
      onClose();
    } catch (err) {
      if (isServerTimeUnknownError(err)) {
        toast.error(formatMessage(serverTimeUnknownMessage));
      } else {
        toast.error(
          err instanceof Error
            ? err.message
            : formatMessage({
                id: "invoicing.waiveV2.error.generic",
                defaultMessage: "Failed to waive this invoice",
              }),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContainer
      open={open}
      onClose={onClose}
      data-testid="invoicing.waiveV2.modal"
    >
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={warningIcon}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.waiveV2.title"
              defaultMessage="Waive Payment"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <FormProvider {...formMethods}>
        <form
          name="waive-invoice-v2-form"
          onSubmit={formMethods.handleSubmit(onSubmit)}
        >
          <DialogBody>
            <Typography variant="h6" className={classes.warningMessage}>
              <FormattedMessage
                id="invoicing.waiveV2.message"
                defaultMessage="Select a reason for the waive"
              />
            </Typography>
            <Box mt={2} ml={2}>
              <RadioGroupField name="reason" options={reasonOptions} required />
            </Box>
            {watchedReason === WAIVE_REASON_VALUES.OTHER && (
              <Grid container>
                <Grid item xs={12}>
                  <TextField
                    name="otherText"
                    label={formatMessage({
                      id: "invoicing.waiveV2.otherText.label",
                      defaultMessage: "Please specify",
                    })}
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={busy}
                    data-testid="invoicing.waiveV2.otherText"
                  />
                </Grid>
              </Grid>
            )}
          </DialogBody>
          <DialogFooter>
            <Button color="secondary" onClick={onClose} disabled={busy}>
              <FormattedMessage
                id="invoicing.waiveV2.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!formMethods.formState.isValid || busy}
              loading={busy}
              data-testid="invoicing.waiveV2.accept"
            >
              <FormattedMessage
                id="invoicing.waiveV2.accept"
                defaultMessage="Waive"
              />
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};
